const STRING_FILTER_COLUMN_OPERATORS = [
  "contains",
  "doesNotContain",
  "endsWith",
  "equals",
  "isEmpty",
  "isNotEmpty",
  "startsWith",
] as const

export type StringFilterColumnOperator =
  (typeof STRING_FILTER_COLUMN_OPERATORS)[number]

export interface StringFilterColumnCondition {
  operator: StringFilterColumnOperator
  value?: string
}

const OPERATORS_WITHOUT_VALUE = new Set<StringFilterColumnOperator>([
  "isEmpty",
  "isNotEmpty",
])

const isStringFilterColumnOperator = (
  value: string
): value is StringFilterColumnOperator =>
  (STRING_FILTER_COLUMN_OPERATORS as readonly string[]).includes(value)

const readOptionalValue = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? ""
  return trimmed || null
}

/**
 * Reads one `{field}_op` + `{field}` column. Unknown operators and comparisons
 * that need a value but do not have one are omitted.
 */
export const readStringFilterColumn = (
  operatorRaw: string | null | undefined,
  valueRaw: string | null | undefined
): StringFilterColumnCondition | null => {
  if (!operatorRaw || !isStringFilterColumnOperator(operatorRaw)) {
    return null
  }

  if (OPERATORS_WITHOUT_VALUE.has(operatorRaw)) {
    return { operator: operatorRaw }
  }

  const value = readOptionalValue(valueRaw)
  if (!value) {
    return null
  }

  return { operator: operatorRaw, value }
}

/**
 * Writes one column. `null` clears the operator and the value. Operators that
 * need no value clear the value. An empty value clears the column.
 */
export const writeStringFilterColumn = (
  condition: { operator: string; value?: string } | null | undefined
): { operator: string | null; value: string | null } => {
  if (!condition || !isStringFilterColumnOperator(condition.operator)) {
    return { operator: null, value: null }
  }

  if (OPERATORS_WITHOUT_VALUE.has(condition.operator)) {
    return { operator: condition.operator, value: null }
  }

  const value = readOptionalValue(condition.value)
  if (!value) {
    return { operator: null, value: null }
  }

  return { operator: condition.operator, value }
}
